// Billing.tsx — workspace-level billing screen (spec D7/M5 task-6, handoff §11; checkout/
// portal wired live in M6 Task 3; upgrade/downgrade/cancel landed in the M6 fix-wave SEV-1
// #1). Current-plan card (name/price/renewal + two usage bars) + 3 plan cards from the real
// `PLAN_LIMITS` catalog (`@repo/shared-types` — there is no `plans` DB table).
//
// `POST /api/billing/checkout` now has TWO outcomes the button has to render differently
// (see that route's own doc comment): a brand-new Polar checkout (`{url}` — full-page
// `location.assign`, never embedded, since it's a Polar-hosted page) when the workspace has
// no entitled subscription yet, or an in-place plan switch (`{updated:true}`) when it
// already does — upgrading AND downgrading both go through the exact same call, the route
// decides which happened. A switch has no URL to redirect to, so it reuses the SAME
// post-checkout poll UX `?upgraded=1` already built (renamed `polling` below to cover both
// triggers) rather than a second bespoke "confirming…" state.
//
// "Manage billing" POSTs `/api/billing/portal`; "Cancel subscription" POSTs
// `/api/billing/cancel` (cancels at the END of the current period, never immediately) behind
// a confirm dialog. Every plan change — upgrade AND downgrade, from the summary card's quick
// button and every `PlanCard` — sits behind its own confirm dialog too (Task 2, dashboard-shell
// feedback cycle): the switch takes effect immediately either way, so this is the one place
// that has to get "what actually happens with money" right (see `lib/billing-copy.ts`). All
// four routes are server-side `BillingProvider` calls (CLAUDE.md provider boundary) — this file
// never touches Polar directly.
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { PLAN_CODES, PLAN_LIMITS } from '@repo/shared-types';
import type {
  BillingSummaryDto,
  CancelResponseDto,
  CheckoutResponseDto,
  PlanCode,
  PortalResponseDto,
  SubscriptionUpdatedDto,
} from '@repo/shared-types';
import { CreditCardIcon } from 'lucide-react';
import { Card } from '@repo/ui/components/card';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import { EmptyState } from '@repo/ui/components/empty-state';
import { useMyWorkspaces } from '../hooks/useMyWorkspaces';
import { Skeleton, SkeletonText } from '@repo/ui/components/skeleton';
import { UsageWarningBanner } from '../components/UsageWarningBanner';
import { BILLING_SUMMARY_QUERY_KEY, useBillingSummary } from '../hooks/useBillingSummary';
import { apiFetch, ApiError } from '../lib/api';
import { planChangeConfirmCopy } from '../lib/billing-copy';
import { useLocale } from '../locales/context';
import type { Catalog } from '../locales/en';
import type { Locale } from '../locales';
import { formatDate as formatDateIntl, formatNumber } from '../locales/format';

/** How long the post-checkout poll keeps trying before giving up and telling the member to
 * refresh manually (spec D1 — the webhook lands asynchronously, this is best-effort UX, never
 * a correctness guarantee: local enforcement always re-checks the real DB on the next call). */
const UPGRADE_POLL_TIMEOUT_MS = 30_000;
const UPGRADE_POLL_INTERVAL_MS = 2_000;

// Bug found live on prod 2026-08-05: a subscription scheduled to cancel at period end
// (`cancelAtPeriodEnd: true`) is still `status: 'active'`, so the plan-switch/upgrade
// buttons below rendered enabled and clicking one 500'd (`AlreadyCanceledSubscription`
// from Polar's `subscriptions.update`, now translated server-side into a 409
// `SUBSCRIPTION_CANCELING`). `t.billing.planChangesPausedReason` is reused verbatim as both
// the disabled buttons' `title` and the 409's error message, so the two surfaces never drift.

/** Sized to the real current-plan card (name/price row + two usage bars) and the three plan
 * cards below it (handoff Shell Anatomy §06). */
function BillingSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <Card className="gap-5 p-6">
        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-col gap-1.5">
            <Skeleton className="h-6 w-32" />
            <SkeletonText className="w-48" />
          </div>
          <Skeleton className="h-9 w-32 rounded-md" />
        </div>
        <div className="flex flex-col gap-3.5">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </Card>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-40 w-full rounded-xl" />
        ))}
      </div>
    </div>
  );
}

function formatDate(iso: string, locale: Locale): string {
  return formatDateIntl(iso, locale, { month: 'long', day: 'numeric', year: 'numeric' });
}

/** The next higher-priced plan after `key`, else `null` when `key` is already the top plan
 * (Growth) — used both for the summary card's "Upgrade to X" label and the usage-bar
 * upgrade hint ("Growth raises it to 10,000 pages"). */
function nextPlan(key: PlanCode): { key: PlanCode; name: string } | null {
  const idx = PLAN_CODES.indexOf(key);
  const next = PLAN_CODES[idx + 1];
  return next ? { key: next, name: PLAN_LIMITS[next].name } : null;
}

/** Renewal/free-forever line under the plan name in the summary card, combining the caps
 * into one string the way the handoff's "Renews {date} · 1 bot · 3,000 messages · 2,000
 * pages" line does. */
function renewalLine(summary: BillingSummaryDto, t: Catalog, locale: Locale): string {
  if (summary.internal) return t.billing.internalRenewalFallback;
  const { caps } = summary.plan;
  // caps are `number | null` on the wire (null = unlimited); internal returns above, but
  // stay null-safe here so a partial-cap plan can never crash the string build.
  const fmt = (n: number | null) => (n === null ? t.billing.unlimitedWord : formatNumber(n, locale));
  const capsSuffix = t.billing.capsSuffix({
    bots: fmt(caps.bots),
    botsCount: caps.bots,
    messages: fmt(caps.msgPerMonth),
    pages: fmt(caps.pages),
  });
  if (summary.plan.key === 'free') return t.billing.freeForever({ capsSuffix });
  return summary.renewsAt
    ? t.billing.renewsOn({ date: formatDate(summary.renewsAt, locale), capsSuffix })
    : capsSuffix;
}

interface UsageBarProps {
  label: string;
  value: number;
  /** null = unlimited (internal workspaces — caps are null on the wire). */
  cap: number | null;
  /** Used only in the ≥90% upgrade hint line ("… of your {noun} limit — …"). */
  noun: string;
  hintUnit: string;
  currentPlan: PlanCode;
  t: Catalog;
  locale: Locale;
}

function UsageBar({ label, value, cap: rawCap, noun, hintUnit, currentPlan, t, locale }: UsageBarProps) {
  const cap = rawCap === null ? Infinity : rawCap;
  // Internal workspaces (spec §A) report `Infinity` caps (per `BillingSummaryDto.plan.caps`'s
  // doc comment) — this screen never actually renders this component for an internal
  // workspace (see the `summary.internal` branch below), but this guard keeps the component
  // itself honest/reusable rather than relying solely on the caller never passing Infinity.
  const isUnlimited = !Number.isFinite(cap);
  // Division-by-zero guard: a 0 cap is already fully consumed, not a NaN/Infinity bar.
  const rawPct = isUnlimited ? 0 : cap <= 0 ? 100 : (value / cap) * 100;
  const displayPct = Math.min(100, Math.max(0, Math.round(rawPct)));
  const isWarn = !isUnlimited && rawPct >= 90;
  const upgrade = nextPlan(currentPlan);

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex justify-between text-[12.5px]">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">
          {formatNumber(value, locale)}{' '}
          {isUnlimited ? t.billing.unlimitedParens : t.billing.ofCap({ cap: formatNumber(cap, locale) })}
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full"
          style={{
            width: `${displayPct}%`,
            background: isWarn ? 'var(--st-paused)' : 'var(--primary)',
          }}
        />
      </div>
      {isWarn && (
        <span className="text-xs" style={{ color: 'var(--st-paused)' }}>
          {t.billing.usageLimitWarning({ pct: displayPct, noun })}
          {upgrade ? t.billing.upgradeRaisesTo({ name: upgrade.name, unit: hintUnit }) : t.billing.limitReached}
        </span>
      )}
    </div>
  );
}

interface PlanCardProps {
  planKey: PlanCode;
  isCurrent: boolean;
  currentPlanKey: PlanCode;
  onUpgrade: (plan: PlanCode) => void;
  pending: boolean;
  /** M6 fix-wave — disables this card's switch/upgrade button (with an explanatory
   * `title`) while the current subscription is scheduled to cancel at period end. */
  switchingDisabled: boolean;
  t: Catalog;
  locale: Locale;
}

function PlanCard({
  planKey,
  isCurrent,
  currentPlanKey,
  onUpgrade,
  pending,
  switchingDisabled,
  t,
  locale,
}: PlanCardProps) {
  const facts = PLAN_LIMITS[planKey];
  // Every line reads off `PLAN_LIMITS`, including the badge one — `planKey === 'free'` used to
  // stand in for it here, which happened to agree with the widget but only by coincidence.
  // This screen and the public pricing page are two renderings of one catalog; whenever plans
  // change, both have to move, and the only way to guarantee that is for neither to restate a
  // plan fact in its own words. Post-badge-opt-in-split (2026-08-11), a paid plan's badge is
  // optional (owner-controlled via the Appearance toggle, off by default), not absent — "No
  // badge" would overclaim the moment the owner turns it back on.
  // Rendered separately from `features` below (not folded into that array) because the badge
  // fact needs different TREATMENT, not just different text: a required badge is a limitation,
  // not something the plan "gets" — a green checkmark on it would read as a benefit and blunt
  // the exact upsell this fact exists to create. Mirrors `pricing.tsx`'s own two branches
  // (muted bullet vs. green check) — two renderings of one catalog, so they must agree in
  // treatment as well as text.
  const features = [
    t.billing.botsUnit({ n: facts.maxBots }),
    t.billing.seatsUnit({ n: facts.members }),
    t.billing.messagesPerMonth({ n: formatNumber(facts.maxMessages, locale) }),
    t.billing.pagesUnit({ n: formatNumber(facts.maxPages, locale) }),
  ];
  const isUpgrade = facts.priceUsd > PLAN_LIMITS[currentPlanKey].priceUsd;
  // Downgrade target (M6 fix-wave SEV-1 #1) — a cheaper PAID plan than the current one.
  // Never `free`: there is no `/api/billing/checkout` target for it (the schema only
  // accepts `starter`/`growth`) — "downgrading to free" is the separate Cancel action
  // below, not a plan switch.
  const isDowngradeTarget =
    planKey !== 'free' && facts.priceUsd < PLAN_LIMITS[currentPlanKey].priceUsd;
  // The current plan is never free while a downgrade target exists (free is the bottom
  // tier), so this is "Switching…" for every downgrade button and "Switching…"/
  // "Redirecting…" (checkout vs. in-place switch) for upgrade buttons, matching exactly
  // what `POST /api/billing/checkout` will actually do for `currentPlanKey`.
  const pendingLabel = currentPlanKey === 'free' ? t.billing.redirectingCta : t.billing.switchingCta;

  return (
    <Card className={`relative gap-2.5 p-5 ${isCurrent ? 'border-[1.5px] border-primary' : ''}`}>
      {isCurrent && (
        <span
          className="absolute -top-[9px] left-4 rounded-full px-2 py-0.5 font-semibold uppercase text-primary-foreground"
          style={{ background: 'var(--primary)', fontSize: '11.5px', letterSpacing: '0.04em' }}
        >
          {t.billing.currentPlanBadge}
        </span>
      )}
      <strong className="text-sm">{facts.name}</strong>
      <div className="flex items-baseline gap-1">
        <span className="font-display text-[26px] font-bold">${facts.priceUsd}</span>
        <span className="text-[12.5px] text-muted-foreground">/mo</span>
      </div>
      <div className="flex flex-col gap-1 text-[13px] text-muted-foreground">
        {features.map((feature) => (
          <span key={feature}>
            <span style={{ color: 'var(--st-ready)' }}>✓</span> {feature}
          </span>
        ))}
        {facts.badgeRequired ? (
          <span>
            <span className="font-semibold">·</span> {t.billing.badgeRequiredText}
          </span>
        ) : (
          <span>
            <span style={{ color: 'var(--st-ready)' }}>✓</span> {t.billing.badgeOptionalText}
          </span>
        )}
      </div>
      {(isUpgrade || isDowngradeTarget) && (
        <button
          type="button"
          disabled={pending || switchingDisabled}
          title={switchingDisabled ? t.billing.planChangesPausedReason : undefined}
          onClick={() => onUpgrade(planKey)}
          className="mt-auto inline-flex h-8 items-center justify-center rounded-md border px-3 text-[13px] font-medium whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-50"
        >
          {pending ? pendingLabel : isUpgrade ? t.billing.upgradeCta : t.billing.switchToCta({ name: facts.name })}
        </button>
      )}
    </Card>
  );
}

export function Billing() {
  const { t, locale } = useLocale();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const upgraded = searchParams.get('upgraded') === '1';
  // A same-page plan switch (M6 fix-wave SEV-1 #1 — `updateSubscription`, no redirect) has
  // no `?upgraded=1` return trip to carry the poll trigger, so the checkout mutation's own
  // `onSuccess` sets this directly when the response is `{updated:true}` instead of a
  // checkout `{url}`. `polling` unifies both triggers so the poll/banner logic below is
  // written exactly once for "a real checkout just completed" AND "a switch just completed".
  const [switched, setSwitched] = useState(false);
  const polling = upgraded || switched;

  // Post-checkout/switch poll (spec D1): the webhook lands asynchronously, so `polling` polls
  // the summary every 2s until the plan actually changes from whatever it was on THIS page
  // load (`baselinePlan`), or `UPGRADE_POLL_TIMEOUT_MS` elapses — never spins forever.
  // `baselinePlan` is captured via React's documented "adjusting state during rendering"
  // pattern (a guarded `setState` call in the render body, not inside `useEffect` —
  // https://react.dev/reference/react/useState#storing-information-from-previous-renders):
  // it fires once, immediately re-renders, then the guard (`baselinePlan === undefined`)
  // never matches again, so there is no loop.
  const [baselinePlan, setBaselinePlan] = useState<PlanCode | undefined>(undefined);
  const [pollTimedOut, setPollTimedOut] = useState(false);

  // Browser-QA finding (2026-08-21): after the final-fix round opened GET /billing/summary to
  // every role (H1 — the rail needs it), this page stopped ever receiving the 403 that fed
  // its FORBIDDEN EmptyState — a member deep-linking /billing saw the full billing screen,
  // against the spec's owner|admin matrix. The role gate therefore has to be CLIENT-side now:
  // the active workspace's role from useMyWorkspaces decides, the same source the rail's
  // workspace menu uses to hide the menu item. The FORBIDDEN branch below stays as belt-and-braces for the routes
  // that ARE still server-gated (checkout/cancel/portal).
  const { data: myWorkspaces } = useMyWorkspaces();
  const activeRole = myWorkspaces?.find((w) => w.isActive)?.role;

  const {
    data: summary,
    isPending,
    error,
  } = useBillingSummary({
    // The function form reads the LIVE query's own data (`query.state.data`), not this call's
    // return value — sidesteps the circularity of "the interval depends on the data this same
    // call produces". Stops the moment the plan no longer matches the baseline.
    refetchInterval: (query) => {
      if (!polling || pollTimedOut) return false;
      const data = query.state.data;
      if (!data || baselinePlan === undefined || data.plan.key === baselinePlan) {
        return UPGRADE_POLL_INTERVAL_MS;
      }
      return false;
    },
  });

  if (polling && summary && baselinePlan === undefined) {
    setBaselinePlan(summary.plan.key);
  }

  const planChanged = baselinePlan !== undefined && summary?.plan.key !== baselinePlan;

  // The timeout is a real external timer (setTimeout), so `setPollTimedOut` runs inside its
  // callback, not synchronously in the effect body — the one `setState`-in-effect the linter
  // allows, since it's genuinely reacting to an external event, not deriving from props/state.
  useEffect(() => {
    if (!polling || pollTimedOut || planChanged) return undefined;
    const timer = setTimeout(() => setPollTimedOut(true), UPGRADE_POLL_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [polling, pollTimedOut, planChanged]);

  const actionConfirmed = polling && planChanged;

  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  // Task 2 (dashboard-shell feedback cycle) — every plan change (upgrade AND downgrade) now
  // sits behind a confirmation dialog, matching "Cancel subscription" below: this is set to
  // the TARGET plan the instant an "Upgrade"/"Switch to X" control is clicked, and the dialog
  // renders (rather than the mutation firing immediately). `checkoutMutation.mutate` only ever
  // runs from the dialog's own confirm button now.
  const [pendingPlanChange, setPendingPlanChange] = useState<PlanCode | null>(null);
  const checkoutMutation = useMutation({
    mutationFn: (plan: PlanCode) =>
      apiFetch<CheckoutResponseDto | SubscriptionUpdatedDto>('/api/billing/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan }),
      }),
    onSuccess: (data) => {
      if ('url' in data) {
        window.location.assign(data.url);
      } else {
        // In-place switch (upgrade OR downgrade, M6 fix-wave SEV-1 #1) — no redirect
        // target, so drive the exact same post-action poll UX a real checkout's
        // `?upgraded=1` return trip already built.
        setSwitched(true);
        setPendingPlanChange(null);
      }
    },
    onError: (err) =>
      setCheckoutError(err instanceof ApiError ? err.message : t.billing.checkoutErrorFallback),
  });

  const [portalError, setPortalError] = useState<string | null>(null);
  const portalMutation = useMutation({
    mutationFn: () => apiFetch<PortalResponseDto>('/api/billing/portal', { method: 'POST' }),
    onSuccess: (data) => window.location.assign(data.url),
    onError: (err) =>
      setPortalError(err instanceof ApiError ? err.message : t.billing.portalErrorFallback),
  });

  // Cancel subscription (M6 fix-wave SEV-1 #1) — cancels at the END of the current billing
  // period, never immediately (`POST /api/billing/cancel`'s own contract). No poll needed
  // afterward: unlike a plan switch, the *displayed* plan/caps don't change at all until the
  // period actually ends — only `cancelAtPeriodEnd` flips, so a targeted refetch is enough.
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const cancelMutation = useMutation({
    mutationFn: () => apiFetch<CancelResponseDto>('/api/billing/cancel', { method: 'POST' }),
    onSuccess: () => {
      setCancelDialogOpen(false);
      void queryClient.invalidateQueries({ queryKey: BILLING_SUMMARY_QUERY_KEY });
    },
    onError: (err) =>
      setCancelError(err instanceof ApiError ? err.message : t.billing.cancelErrorFallback),
  });

  // Copy for the plan-change confirmation dialog below — `null` while no change is pending, or
  // before `summary` has loaded (the dialog can only ever open from a button that requires
  // `summary` to render in the first place, but this keeps the render below null-safe without
  // a non-null assertion). `isUpgrade` mirrors `PlanCard`'s own price comparison exactly.
  const pendingPlanCopy =
    summary && pendingPlanChange
      ? planChangeConfirmCopy({
          currentPlanKey: summary.plan.key,
          targetPlanKey: pendingPlanChange,
          isUpgrade: PLAN_LIMITS[pendingPlanChange].priceUsd > PLAN_LIMITS[summary.plan.key].priceUsd,
          t,
        })
      : null;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between gap-3">
          <h1 className="font-display text-2xl font-bold">{t.billing.pageTitle}</h1>
          {summary?.canManageBilling && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={portalMutation.isPending}
              onClick={() => {
                setPortalError(null);
                portalMutation.mutate();
              }}
            >
              {portalMutation.isPending ? t.billing.openingCta : t.billing.manageBillingCta}
            </Button>
          )}
        </div>
        {portalError && <p className="text-[13px] text-destructive">{portalError}</p>}
      </div>

      {activeRole === 'member' && (
        <EmptyState
          icon={CreditCardIcon}
          title={t.billing.memberGateTitle}
          description={t.billing.memberGateDescription}
        />
      )}
      {activeRole !== 'member' && isPending && <BillingSkeleton />}
      {activeRole !== 'member' && error &&
        // M2 fix (final-fix round) — a plain member deep-linking `/billing` used to see the
        // generic `error.message` text (raw "Your role cannot do this" red text). Same
        // `ApiError.code === 'FORBIDDEN'` branch as `Members.tsx`'s own 403 EmptyState, since
        // `GET /api/billing/summary` no longer 403s for a member (H1) but the checkout/cancel/
        // portal mutations still do, and this screen's own `useBillingSummary` can still land
        // here on other 403 paths.
        (error instanceof ApiError && error.code === 'FORBIDDEN' ? (
          <EmptyState
            icon={CreditCardIcon}
            title={t.billing.memberGateTitle}
            description={t.billing.memberGateDescription}
          />
        ) : (
          <p className="text-destructive">{error.message}</p>
        ))}

      {activeRole !== 'member' && polling && (
        <div
          role="status"
          className="rounded-lg border px-4 py-2.5 text-[13px]"
          style={{
            borderColor: 'var(--st-ready)',
            background: 'color-mix(in oklch, var(--st-ready) 12%, transparent)',
          }}
        >
          {actionConfirmed
            ? t.billing.nowOnPlan({ name: String(summary?.plan.name) })
            : pollTimedOut
              ? t.billing.finishingUp
              : t.billing.confirmingWithPolar}
        </div>
      )}

      {activeRole !== 'member' && summary && !summary.internal && <UsageWarningBanner summary={summary} />}

      {activeRole !== 'member' && summary && (
        <>
          <Card className="gap-5 p-6">
            <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
              <div className="flex flex-col gap-0.5">
                <div className="flex items-baseline gap-2.5">
                  <strong className="font-display text-xl">
                    {summary.internal ? t.billing.internalWorkspaceLabel : summary.plan.name}
                  </strong>
                  {!summary.internal && (
                    <span className="text-sm text-muted-foreground">
                      {t.billing.monthlyPriceLabel({ price: summary.plan.priceUsd })}
                    </span>
                  )}
                </div>
                <span className="text-[12.5px] text-muted-foreground">
                  {summary.internal
                    ? t.billing.internalWorkspaceInline
                    : renewalLine(summary, t, locale)}
                </span>
                {!summary.internal && summary.cancelAtPeriodEnd && (
                  <span className="flex flex-col gap-0.5">
                    <span
                      className="text-[12.5px] font-medium"
                      style={{ color: 'var(--st-paused)' }}
                    >
                      {t.billing.cancelsAtPeriodEnd}
                    </span>
                    <span className="text-[12.5px] text-muted-foreground">
                      {t.billing.planChangesPausedNotice}
                    </span>
                  </span>
                )}
              </div>
              {!summary.internal && nextPlan(summary.plan.key) && (
                <button
                  type="button"
                  disabled={checkoutMutation.isPending || summary.cancelAtPeriodEnd}
                  title={summary.cancelAtPeriodEnd ? t.billing.planChangesPausedReason : undefined}
                  onClick={() => {
                    setCheckoutError(null);
                    setPendingPlanChange(nextPlan(summary.plan.key)!.key);
                  }}
                  className="inline-flex h-9 w-full shrink-0 items-center justify-center rounded-md px-4 text-[13.5px] font-medium whitespace-nowrap text-primary-foreground shadow-el-1 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                  style={{ background: 'var(--primary)' }}
                >
                  {checkoutMutation.isPending
                    ? summary.plan.key === 'free'
                      ? t.billing.redirectingCta
                      : t.billing.switchingCta
                    : t.billing.upgradeToCta({ name: nextPlan(summary.plan.key)!.name })}
                </button>
              )}
            </div>

            <div className="flex flex-col gap-3.5">
              <UsageBar
                label={t.billing.messagesThisMonthLabel}
                value={summary.usage.messagesThisMonth}
                cap={summary.plan.caps.msgPerMonth}
                noun={t.billing.messageNoun}
                hintUnit={t.billing.messagesUnit({
                  n: formatNumber(
                    PLAN_LIMITS[nextPlan(summary.plan.key)?.key ?? summary.plan.key].maxMessages,
                    locale,
                  ),
                })}
                currentPlan={summary.plan.key}
                t={t}
                locale={locale}
              />
              <UsageBar
                label={t.billing.pagesIndexedLabel}
                value={summary.usage.pagesTotal}
                cap={summary.plan.caps.pages}
                noun={t.billing.pageNoun}
                hintUnit={t.billing.pagesUnit({
                  n: formatNumber(
                    PLAN_LIMITS[nextPlan(summary.plan.key)?.key ?? summary.plan.key].maxPages,
                    locale,
                  ),
                })}
                currentPlan={summary.plan.key}
                t={t}
                locale={locale}
              />
            </div>

            {!summary.internal && summary.plan.key !== 'free' && !summary.cancelAtPeriodEnd && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-auto w-fit self-start px-0 text-[12.5px] text-destructive hover:bg-transparent hover:text-destructive"
                onClick={() => {
                  setCancelError(null);
                  setCancelDialogOpen(true);
                }}
              >
                {t.billing.cancelSubscriptionCta}
              </Button>
            )}
          </Card>

          {summary.internal ? (
            <Card className="gap-2 p-6">
              <strong className="font-display text-lg">{t.billing.internalWorkspaceHeading}</strong>
              <p className="text-[13px] text-muted-foreground">{t.billing.internalWorkspaceBody}</p>
            </Card>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                {PLAN_CODES.map((key) => (
                  <PlanCard
                    key={key}
                    planKey={key}
                    isCurrent={key === summary.plan.key}
                    currentPlanKey={summary.plan.key}
                    onUpgrade={(plan) => {
                      setCheckoutError(null);
                      setPendingPlanChange(plan);
                    }}
                    pending={checkoutMutation.isPending && checkoutMutation.variables === key}
                    switchingDisabled={summary.cancelAtPeriodEnd}
                    t={t}
                    locale={locale}
                  />
                ))}
              </div>

              <p className="text-[12.5px] text-muted-foreground">{t.billing.flatPricingNote}</p>
            </>
          )}
        </>
      )}

      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.billing.cancelDialogTitle}</DialogTitle>
            <DialogDescription>
              {t.billing.cancelDialogDescription({
                planName: summary?.plan.name ?? t.billing.yourCurrentPlanFallback,
                renewsDate: summary?.renewsAt ? formatDate(summary.renewsAt, locale) : null,
              })}
            </DialogDescription>
          </DialogHeader>
          {cancelError && <p className="text-[13px] text-destructive">{cancelError}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCancelDialogOpen(false)}>
              {t.billing.keepSubscriptionCta}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={cancelMutation.isPending}
              onClick={() => cancelMutation.mutate()}
            >
              {cancelMutation.isPending ? t.billing.cancelingCta : t.billing.cancelSubscriptionCta}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task 2 (dashboard-shell feedback cycle) — every plan change, upgrade or downgrade,
          now confirms first: this used to fire `POST /api/billing/checkout` on a single click,
          a real money action with an immediate effect and no dialog, unlike Cancel above.
          `planChangeConfirmCopy` states the actual behaviour (`apps/api/src/routes/billing.ts`'s
          own doc comment): an already-paying workspace switches in place with no charge today
          (Polar prorates onto the next invoice), while a Free workspace is a brand-new hosted
          checkout — a real charge, just made on Polar's page. */}
      <Dialog
        open={pendingPlanChange !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingPlanChange(null);
            setCheckoutError(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{pendingPlanCopy?.title ?? t.billing.changePlanFallbackTitle}</DialogTitle>
            <DialogDescription>{pendingPlanCopy?.description}</DialogDescription>
          </DialogHeader>
          {checkoutError && <p className="text-[13px] text-destructive">{checkoutError}</p>}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setPendingPlanChange(null);
                setCheckoutError(null);
              }}
            >
              {t.billing.keepCurrentPlanCta}
            </Button>
            <Button
              type="button"
              disabled={checkoutMutation.isPending}
              onClick={() => {
                if (pendingPlanChange) checkoutMutation.mutate(pendingPlanChange);
              }}
            >
              {checkoutMutation.isPending
                ? summary?.plan.key === 'free'
                  ? t.billing.redirectingCta
                  : t.billing.switchingCta
                : (pendingPlanCopy?.confirmLabel ?? t.billing.confirmFallbackCta)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
